import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Card, Button, Typography, Space, Divider, InputNumber } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  Info,
  ArrowRight,
  LogOut,
  X,
  Crown,
  Zap,
  Sparkles,
  Check,
  CreditCard,
} from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { useCurrency } from '../../../hooks/useCurrency';
import { openRazorpayCheckout } from '../../../utils/razorpayCheckout';
import toast from 'react-hot-toast';
import { DASHBOARD_BASE } from '../../../config/featureFlags';
import { useDashTheme } from '../hooks/useDashTheme';
import { ShieldCheck, Database, Users, CalendarX, Clock as ClockIcon } from 'lucide-react';

const { Title, Paragraph, Text } = Typography;

/**
 * PlanExpiredOverlay
 * ------------------
 * Self-contained copy of the original Dashboard plan-expiry / renewal flow
 * (Dashboard.js). The payment logic, coupon verification and renewal checkout
 * (Stripe + Razorpay) are reused VERBATIM — only lifted into a prop-driven
 * component so the V2 shell (LayoutV1) can render the exact same blocking modal
 * the legacy Dashboard shows when a plan is expired/restricted.
 *
 * No backend changes: hits the same /api/v1/public/(billing|razorpay-billing)/*
 * endpoints the original used.
 *
 * Props:
 *  - restricted   : boolean  (computed by the shell: trial/plan expired)
 *  - orgLoaded    : boolean
 *  - organization : object   (the /billing/profile org payload)
 *  - user         : object   (auth user)
 *  - logout       : fn       (from useAuth)
 */
export default function PlanExpiredOverlay({ restricted, orgLoaded, organization, user, logout }) {
  const api = useApi();
  const t = useDashTheme();

  // Currency derived from the org billing profile — same source as the original
  // (orgData.orgPlanCurrency, falling back to gateway → INR/USD).
  const billingCurrency =
    organization?.orgPlanCurrency ||
    (organization?.paymentGateway === 'razorpay' ? 'INR' : 'USD');
  const { formatPrice, getPrice, symbol } = useCurrency({
    billingCurrency,
    paymentGateway: organization?.paymentGateway,
  });

  // ── Renewal / plan-selection state (verbatim from Dashboard.js) ──
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [upgradeMemberCount, setUpgradeMemberCount] = useState({}); // { 'standard': 1, 'premium': 1 }
  const [selectedPlanForRenewal, setSelectedPlanForRenewal] = useState(null);

  // ── Coupon state ──
  const [couponCode, setCouponCode] = useState('');
  const [actualCouponId, setActualCouponId] = useState(null);
  const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [couponVerifying, setCouponVerifying] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const couponTimeoutRef = useRef(null);

  // Disable page scroll while the blocking overlay is active (verbatim).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const original = document.body.style.overflow;
    if (restricted && orgLoaded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = original || '';
    }
    return () => {
      document.body.style.overflow = original || '';
    };
  }, [restricted, orgLoaded]);

  // ── Coupon verification (verbatim from Dashboard.js verifyCouponCode) ──
  const verifyCouponCode = async (code) => {
    if (!code || code.trim() === '') {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      // Get current member count (total slots = admin + members)
      let totalSeats = undefined;
      if (organization) {
        const isFree = organization?.orgPlanSelection === 'free';
        const now = new Date();
        const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
        const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;

        let currentSlots;
        if (isFree && freeTrialExpired) {
          currentSlots = organization?.usedMemberSlots || 5;
        } else {
          currentSlots = organization?.paidMemberSlots || organization?.usedMemberSlots || 5;
        }

        // Use selected plan's member count if available
        if (selectedPlanForRenewal && upgradeMemberCount[selectedPlanForRenewal]) {
          totalSeats = upgradeMemberCount[selectedPlanForRenewal] + 1;
        } else {
          totalSeats = currentSlots;
        }
      }

      const isRazorpay = organization?.paymentGateway === 'razorpay';
      const verifyUrl = isRazorpay
        ? '/api/v1/public/razorpay-billing/verify-coupon'
        : '/api/v1/public/billing/verify-coupon';

      const response = await api.post(verifyUrl, {
        couponCode: code.trim(),
        memberCount: totalSeats,
        organizationId: organization?._id,
      });

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || 'Failed to verify coupon');
    } finally {
      setCouponVerifying(false);
    }
  };

  // ── Plan selection / renewal checkout (verbatim from Dashboard.js handlePlanSelection) ──
  const handlePlanSelection = async (plan) => {
    setProcessingPlan(plan);
    try {
      const now = new Date();
      const isFree = organization?.orgPlanSelection === 'free';
      const orgStatus = organization?.subscriptionStatus;
      const orgPeriodEnd = organization?.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
      const orgExpired = !isFree && (
        ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(orgStatus) ||
        (orgPeriodEnd && now > orgPeriodEnd)
      );

      // If organization subscription expired OR free plan trial expired, use organization-level checkout
      const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
      const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;

      if (orgExpired || freeTrialExpired) {
        // Only org_admin can renew organization subscription
        if (user?.role === 'org_admin' || user?.role === 'main_admin') {
          // Use organization-level subscription checkout for renewal
          let currentSlots, memberCount;

          if (isFree && freeTrialExpired) {
            // Free plan renewal: pay for all current members (usedMemberSlots)
            currentSlots = organization?.usedMemberSlots || 1;
            const minMemberCount = Math.max(1, currentSlots - 1);
            memberCount = upgradeMemberCount[plan] || minMemberCount;
          } else {
            // Paid plan renewal: maintain same paid slots
            currentSlots = organization?.paidMemberSlots || 1;
            const minMemberCount = Math.max(1, currentSlots - 1);
            memberCount = upgradeMemberCount[plan] || minMemberCount;
          }

          const planAmount = getPrice(plan);
          const totalSeats = memberCount + 1; // +1 for admin

          toast.loading(`Creating checkout for ${totalSeats} seats (${memberCount} members + 1 admin)...`, { id: 'renewal-loading' });

          // Try Razorpay first (for Indian users)
          let rzpRes;
          try {
            rzpRes = await api.post('/api/v1/public/razorpay-billing/organization-subscription-checkout', {
              userId: user?.id,
              plan: plan,
              memberCount: memberCount,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
          } catch { rzpRes = null; }

          if (rzpRes?.data?.success && (rzpRes?.data?.data?.subscriptionId || rzpRes?.data?.data?.orderId)) {
            toast.dismiss('renewal-loading');
            const rzpData = rzpRes.data.data;
            openRazorpayCheckout({
              razorpayKeyId: rzpData.razorpayKeyId,
              amount: rzpData.amount,
              currency: rzpData.currency,
              orderId: rzpData.orderId,
              subscriptionId: rzpData.subscriptionId,
              customerName: rzpData.customerName,
              customerEmail: rzpData.customerEmail,
              notes: rzpData.notes,
              description: `TeamGrid ${plan} Plan Renewal`,
              onSuccess: async (paymentResponse) => {
                try {
                  const verifyRes = await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                    razorpay_order_id: paymentResponse.razorpay_order_id,
                    razorpay_payment_id: paymentResponse.razorpay_payment_id,
                    razorpay_signature: paymentResponse.razorpay_signature,
                    razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                    userId: user?.id,
                    organizationId: rzpData.notes.organizationId,
                    plan, memberCount, totalSeats: rzpData.totalSeats,
                    type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'organization_subscription',
                  });
                  if (verifyRes.data.success) {
                    if (verifyRes.data.data.token) {
                      localStorage.setItem('token', verifyRes.data.data.token);
                      localStorage.setItem('refreshToken', verifyRes.data.data.refreshToken);
                      api.updateToken(verifyRes.data.data.token);
                    }
                    toast.success('Payment successful!');
                    setTimeout(() => { window.location.href = `${DASHBOARD_BASE}?payment=success`; }, 100);
                  } else {
                    toast.error(verifyRes.data.message || 'Payment verification failed');
                    setProcessingPlan(null);
                  }
                } catch { toast.error('Payment verification failed'); setProcessingPlan(null); }
              },
              onFailure: (error) => { toast.error(error?.description || 'Payment failed'); setProcessingPlan(null); },
              onDismiss: () => { setProcessingPlan(null); },
            });
            return;
          }

          // Stripe flow (international users)
          const response = await api.post('/api/v1/public/billing/organization-subscription-checkout', {
            userId: user?.id,
            plan: plan,
            memberCount: memberCount,
            ...(actualCouponId && { couponCode: actualCouponId })
          });

          if (response.data?.success) {
            toast.dismiss('renewal-loading');
            const url = response.data?.data?.url;
            if (url) {
              window.location.href = url;
            } else {
              toast.error('Checkout URL not received');
              setProcessingPlan(null);
            }
          } else {
            toast.dismiss('renewal-loading');
            toast.error(response.data?.message || 'Failed to start checkout');
            setProcessingPlan(null);
          }
          return;
        } else {
          // Regular members can't renew organization subscription
          toast.error('Organization subscription has expired. Please contact your administrator to renew.');
          setProcessingPlan(null);
          return;
        }
      }

      // User-level subscription checkout (for individual user subscription)
      if (plan === 'free') {
        const response = await api.post('/api/v1/public/billing/user-subscription-checkout', {
          userId: user?.id, plan,
          ...(couponCode && { couponCode: couponCode.trim() })
        });
        if (response.data?.success) {
          const token = response.data?.data?.token;
          const refreshToken = response.data?.data?.refreshToken;
          if (token && refreshToken) {
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
          }
          window.location.href = `${DASHBOARD_BASE}`;
        } else {
          toast.error(response.data?.message || 'Failed to start checkout');
        }
      } else {
        // Paid user plan - try Razorpay first
        let rzpUserRes;
        try {
          rzpUserRes = await api.post('/api/v1/public/razorpay-billing/user-subscription-checkout', {
            userId: user?.id, plan,
            ...(couponCode && { couponCode: couponCode.trim() })
          });
        } catch { rzpUserRes = null; }

        if (rzpUserRes?.data?.success && (rzpUserRes?.data?.data?.subscriptionId || rzpUserRes?.data?.data?.orderId)) {
          const rzpData = rzpUserRes.data.data;
          openRazorpayCheckout({
            razorpayKeyId: rzpData.razorpayKeyId,
            subscriptionId: rzpData.subscriptionId,
            orderId: rzpData.orderId,
            amount: rzpData.amount,
            currency: rzpData.currency,
            customerName: rzpData.customerName,
            customerEmail: rzpData.customerEmail,
            notes: rzpData.notes,
            description: `TeamGrid ${plan} Plan`,
            onSuccess: async (paymentResponse) => {
              try {
                const verifyRes = await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                  userId: user?.id,
                  organizationId: rzpData.notes.organizationId,
                  plan, totalSeats: '1', type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'user_subscription',
                });
                if (verifyRes.data.success) {
                  if (verifyRes.data.data.token) {
                    localStorage.setItem('token', verifyRes.data.data.token);
                    localStorage.setItem('refreshToken', verifyRes.data.data.refreshToken);
                  }
                  toast.success('Payment successful!');
                  setTimeout(() => { window.location.href = `${DASHBOARD_BASE}?payment=success`; }, 100);
                } else { toast.error('Payment verification failed'); }
              } catch { toast.error('Payment verification failed'); }
            },
            onFailure: (error) => { toast.error(error?.description || 'Payment failed'); },
            onDismiss: () => { setProcessingPlan(null); },
          });
        } else {
          // Stripe flow
          const response = await api.post('/api/v1/public/billing/user-subscription-checkout', {
            userId: user?.id, plan,
            ...(couponCode && { couponCode: couponCode.trim() })
          });
          if (response.data?.success) {
            const url = response.data?.data?.url;
            if (url) { window.location.href = url; }
            else { toast.error('Checkout URL not received'); }
          } else { toast.error(response.data?.message || 'Failed to start checkout'); }
        }
      }
    } catch (error) {
      console.error('Plan selection error:', error);
      toast.error(error.response?.data?.message || 'Failed to process plan selection');
    } finally {
      setProcessingPlan(null);
    }
  };

  const locked = Boolean(restricted && orgLoaded && typeof document !== 'undefined');
  // While locked the app chrome (top bar, rail) is hidden outright — a blurred
  // search box and bell behind the wall read as "still usable". Class-based so
  // it reverts the instant the plan is renewed.
  useEffect(() => {
    if (!locked || typeof document === 'undefined') return undefined;
    document.body.classList.add('tg-plan-locked');
    return () => document.body.classList.remove('tg-plan-locked');
  }, [locked]);
  if (!locked) return null;

  return createPortal(
    <Modal
      open={true}
      onCancel={() => {}}
      footer={null}
      closable={false}
      width={700}
      centered
      className="subscription-expiry-modal"
      zIndex={3000}
      styles={{
        mask: { backgroundColor: t.isDark ? 'rgba(10, 12, 16, 0.94)' : 'rgba(20, 26, 38, 0.86)', backdropFilter: 'blur(14px)' },
        content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 },
        body: { padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', maxHeight: '90vh', overflowY: 'auto' }
      }}
    >
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="bg-gradient-to-br from-zinc-900/98 via-zinc-800/98 to-zinc-900/98 border border-white/10 rounded-3xl p-6 backdrop-blur-xl shadow-2xl w-full max-w-[700px] mx-auto max-h-[90vh] overflow-y-auto"
          style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset' }}
        >
          {!showPlanModal ? (
            (() => {
              /* ── The first screen, rebuilt 2026-08-30 ─────────────────────
                 Same anatomy as every DashboardV2 head (rail · tile · kicker ·
                 title · chips), and it SAYS what happened and what happens
                 now, instead of a generic "plan has expired". Renew / Logout
                 behaviour is unchanged. */
              const now = new Date();
              const plan = organization?.orgPlanSelection || null;
              const isFree = plan === 'free';
              const PLAN_NAME = { free: 'Free trial', standard: 'Team', premium: 'Advanced' };
              const status = String(organization?.subscriptionStatus || organization?.userSubscriptionStatus || '').toLowerCase();
              // The date that actually tripped the restriction: for a free plan the
              // shell checks the USER's end date; for paid plans the org period.
              // Only a date in the past is worth naming — a future one means the
              // block came from status (past_due/cancelled), not from the calendar.
              const candidates = isFree
                ? [organization?.userSubscriptionEndDate, organization?.userTrialEndDate, organization?.trialEnd]
                : [organization?.currentPeriodEnd, organization?.userSubscriptionEndDate, organization?.trialEnd];
              const endDate = candidates.map((d) => (d ? new Date(d) : null)).find((d) => d && Number.isFinite(d.getTime()) && d <= now) || null;
              const endLabel = endDate ? endDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;
              const daysAgo = endDate ? Math.max(0, Math.floor((now - endDate) / 86400000)) : null;
              const pastDue = status === 'past_due' || status === 'unpaid';
              const cancelled = status === 'canceled' || status === 'cancelled';
              const isAdmin = user?.role === 'org_admin' || user?.role === 'main_admin';
              const seats = organization?.paidMemberSlots || organization?.usedMemberSlots || null;
              const gateway = organization?.paymentGateway === 'razorpay' ? 'Razorpay' : organization?.paymentGateway === 'stripe' ? 'Stripe' : null;
              const amber = t.isDark ? '#F0B24A' : '#D97706';
              const red = t.status.negative;
              const tone = pastDue ? red : amber;
              const kicker = isFree ? 'TeamGrid · Trial ended' : pastDue ? 'TeamGrid · Payment failed' : cancelled ? 'TeamGrid · Subscription cancelled' : 'TeamGrid · Subscription ended';
              const title = isFree
                ? (endLabel ? `Your free trial ended on ${endLabel}` : 'Your free trial has ended')
                : pastDue
                  ? 'Your last payment did not go through'
                  : (endLabel ? `Your ${PLAN_NAME[plan] || 'plan'} subscription ended on ${endLabel}` : 'Your subscription has ended');
              const lead = isFree
                ? 'Pick a plan to keep every report, summary and insight your team has built up — nothing has been deleted.'
                : pastDue
                  ? 'The renewal charge was declined by the bank. Renew now to unlock the team immediately; nothing has been deleted.'
                  : 'Access for members is paused until the plan is renewed. Everything tracked so far is safe and picks up exactly where it left off.';
              const chips = [
                plan ? { label: `${PLAN_NAME[plan] || plan} plan`, dot: tone } : null,
                endLabel ? { label: `${isFree ? 'trial ended' : 'ended'} ${endLabel}${daysAgo ? ` · ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago` : ''}` } : null,
                seats ? { label: `${seats} seat${seats === 1 ? '' : 's'}` } : null,
                gateway ? { label: gateway } : null,
              ].filter(Boolean);
              const rows = [
                { icon: <Users size={15} />, c: red, title: 'Members are paused', sub: isAdmin ? 'They cannot sign in until the plan is renewed; admins keep access to renew.' : 'Ask an admin to renew — they keep access to Billing.' },
                { icon: <Database size={15} />, c: t.status.positive, title: 'Your data is safe', sub: 'Tracked activity, summaries, projects and settings are all kept. Nothing is deleted.' },
                { icon: <ShieldCheck size={15} />, c: t.c.primary, title: 'Renewal is instant', sub: 'Access returns the moment payment clears — no re-setup, same seats, same history.' },
              ];
              const Chip = ({ label, dot }) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 650, padding: '6px 13px', borderRadius: 999, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, color: t.c.textSecondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {dot ? <i style={{ width: 7, height: 7, borderRadius: 99, background: dot }} /> : null}{label}
                </span>
              );
              return (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                  style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, background: t.c.surface, color: t.c.text, border: `1px solid ${t.c.border}`, fontFamily: t.font.family, margin: -24 }}>
                  <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: tone }} />
                  <span aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(640px 220px at 8% -40%, ${tone}${t.isDark ? '2e' : '22'}, transparent 60%), radial-gradient(420px 160px at 100% 110%, ${tone}${t.isDark ? '18' : '10'}, transparent 60%)` }} />
                  <style>{`@keyframes tgLockPulse{0%{transform:scale(.9);opacity:.9}70%{transform:scale(1.25);opacity:0}100%{opacity:0}}`}</style>
                  <div style={{ position: 'relative', padding: '26px 28px 22px' }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <span aria-hidden="true" style={{ position: 'relative', width: 64, height: 64, borderRadius: 18, flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(135deg, color-mix(in srgb, ${tone} 78%, #fff), ${tone} 58%, color-mix(in srgb, ${tone} 80%, #000))`, boxShadow: `0 14px 30px -12px ${tone}, 0 0 0 6px ${tone}1a` }}>
                        <span aria-hidden="true" style={{ position: 'absolute', inset: -10, borderRadius: 26, border: `2px solid ${tone}55`, animation: 'tgLockPulse 2.4s ease-out infinite' }} />
                        {pastDue ? <AlertTriangle size={28} strokeWidth={2.4} /> : <CalendarX size={28} strokeWidth={2.4} />}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: tone }}>{kicker}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 999, background: tone, color: '#fff' }}>
                            <i style={{ width: 6, height: 6, borderRadius: 99, background: '#fff', opacity: 0.9 }} />{pastDue ? 'Action needed' : 'Access paused'}
                          </span>
                        </div>
                        <h2 style={{ margin: '6px 0 0', fontSize: 27, fontWeight: 750, letterSpacing: '-0.8px', lineHeight: 1.15, color: t.c.text }}>{title}</h2>
                        <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.6, color: t.c.textSecondary, maxWidth: '62ch' }}>{lead}</p>
                        {chips.length ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>{chips.map((c, i) => <Chip key={i} {...c} />)}</div> : null}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 20 }}>
                      {rows.map((r) => (
                        <div key={r.title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 13px', borderRadius: 12, border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted }}>
                          <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center', color: r.c, background: `${r.c}1f`, border: `1px solid ${r.c}44` }}>{r.icon}</span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: t.c.text }}>{r.title}</span>
                            <span style={{ display: 'block', fontSize: 12, lineHeight: 1.5, color: t.c.textSecondary, marginTop: 2 }}>{r.sub}</span>
                          </span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 22, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => setShowPlanModal(true)}
                        style={{ flex: '1 1 260px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, height: 46, padding: '0 18px', borderRadius: 12, border: 0, cursor: 'pointer', fontFamily: t.font.family, fontSize: 15, fontWeight: 700, color: '#fff', background: t.c.primary, boxShadow: `0 8px 20px -10px ${t.c.primary}` }}>
                        <ArrowRight size={17} /> {isFree ? 'Choose a plan' : pastDue ? 'Renew & unlock now' : 'Renew plan'}
                      </button>
                      <button type="button" onClick={logout}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 46, padding: '0 18px', borderRadius: 12, cursor: 'pointer', fontFamily: t.font.family, fontSize: 14, fontWeight: 600, color: t.c.text, background: 'transparent', border: `1px solid ${t.c.borderStrong}` }}>
                        <LogOut size={15} /> Log out
                      </button>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: t.c.textSecondary }}>
                      <ClockIcon size={12} /> Renewals apply immediately · same seats, same data · questions: hello@teamgrid.ai
                    </div>
                  </div>
                </motion.div>
              );
            })()
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="space-y-4"
            >
              {/* Close Button */}
              <div className="flex justify-end">
                <Button
                  type="text"
                  icon={<X className="w-5 h-5" />}
                  onClick={() => {
                    setShowPlanModal(false);
                    setUpgradeMemberCount({});
                    setSelectedPlanForRenewal(null);
                    setCouponCode('');
                    setActualCouponId(null);
                    setCouponValid(null);
                    setCouponDiscount(null);
                  }}
                  className="text-white/60 hover:text-white hover:bg-white/10"
                  style={{ borderRadius: '8px' }}
                />
              </div>

              {/* Plan Selection Header */}
              <div className="text-center mb-3">
                <Title level={4} className="!text-white !mb-1 !text-lg !font-bold">
                  Select a Plan to Renew
                </Title>
                <Paragraph className="!text-gray-400 !text-xs !mb-0">
                  Choose a plan that best fits your team's needs
                </Paragraph>
              </div>

              {/* Plan Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(() => {
                  if (!organization?.orgPlanSelection) return [];
                  const now = new Date();
                  const isFree = organization.orgPlanSelection === 'free';

                  // Check user-level expiry
                  const userStatus = organization.userSubscriptionStatus;
                  const userEndDate = organization.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
                  const userExpired = !isFree && (
                    ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(userStatus) ||
                    (userEndDate && now > userEndDate)
                  );

                  // Check organization-level expiry
                  const orgStatus = organization.subscriptionStatus;
                  const orgPeriodEnd = organization.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
                  const orgExpired = !isFree && (
                    ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(orgStatus) ||
                    (orgPeriodEnd && now > orgPeriodEnd)
                  );

                  const isExpired = userExpired || orgExpired;

                  if (isExpired) {
                    return ['standard', 'premium'];
                  }
                  switch (organization.orgPlanSelection) {
                    case 'free':
                      return ['standard', 'premium'];
                    case 'standard':
                      return ['premium'];
                    case 'premium':
                      return ['standard'];
                    default:
                      return [];
                  }
                })().map((plan, index) => (
                  <motion.div
                    key={plan}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                  >
                    <Card
                      className={`border-2 transition-all cursor-pointer h-full ${
                        selectedPlanForRenewal === plan
                          ? plan === 'premium'
                            ? 'border-purple-400 bg-purple-500/15'
                            : plan === 'standard'
                            ? 'border-blue-400 bg-blue-500/15'
                            : 'border-white/40 bg-white/10'
                          : plan === 'premium'
                          ? 'border-purple-500/40 bg-purple-500/10 hover:border-purple-400/60 hover:bg-purple-500/15'
                          : plan === 'standard'
                          ? 'border-blue-500/40 bg-blue-500/10 hover:border-blue-400/60 hover:bg-blue-500/15'
                          : 'border-white/20 bg-white/5 hover:bg-white/10'
                      }`}
                      style={{ borderRadius: '16px' }}
                      onClick={() => setSelectedPlanForRenewal(plan)}
                    >
                      <div className="text-center">
                        <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                          plan === 'premium' ? 'bg-purple-500/20' : plan === 'standard' ? 'bg-blue-500/20' : 'bg-white/10'
                        }`}>
                          {plan === 'premium' ? (
                            <Crown className={`w-5 h-5 ${plan === 'premium' ? 'text-purple-400' : 'text-white'}`} />
                          ) : plan === 'standard' ? (
                            <Zap className="w-5 h-5 text-blue-400" />
                          ) : (
                            <Sparkles className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <Title level={4} className={`!mb-0.5 !text-base !font-bold ${
                          plan === 'premium' ? '!text-purple-300' : plan === 'standard' ? '!text-blue-300' : '!text-white'
                        }`}>
                          {plan.charAt(0).toUpperCase() + plan.slice(1)}
                        </Title>
                        <div className="mb-2">
                          <span className="text-xl font-bold text-white">
                            {formatPrice(plan)}
                          </span>
                          <span className="text-gray-400 text-xs ml-1">/month</span>
                        </div>
                        <Space direction="vertical" size="small" className="w-full mb-2 text-left">
                          {plan === 'premium' ? (
                            <>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                <span>All features</span>
                              </div>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                <span>Org Intelligence</span>
                              </div>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                <span>Priority support</span>
                              </div>
                            </>
                          ) : plan === 'standard' ? (
                            <>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                                <span>All features</span>
                              </div>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                                <span>Unlimited members</span>
                              </div>
                              <div className="flex items-center text-gray-400 text-sm">
                                <span className="w-4 h-4 mr-2 flex-shrink-0 text-center">✗</span>
                                <span>No Org Intelligence</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center text-gray-300 text-sm">
                                <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                                <span>1 month full AI access</span>
                              </div>
                            </>
                          )}
                        </Space>

                        {/* Member Count Input Section - Only for Standard and Premium */}
                        {(plan === 'standard' || plan === 'premium') && (
                          <div className="mb-2 p-2 rounded-lg border bg-white/5 border-white/10">
                            <div className="mb-1.5">
                              <Text className="text-xs block mb-1 font-medium text-gray-300">
                                Number of Team Members
                              </Text>
                              {(() => {
                                // Calculate minimum member count (current used - 1 for admin)
                                const isFree = organization?.orgPlanSelection === 'free';
                                const now = new Date();
                                const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
                                const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;

                                let currentSlots;
                                if (isFree && freeTrialExpired) {
                                  currentSlots = organization?.usedMemberSlots || 5;
                                } else {
                                  currentSlots = organization?.paidMemberSlots || organization?.usedMemberSlots || 5;
                                }

                                const minMemberCount = Math.max(1, currentSlots - 1);
                                const memberCount = upgradeMemberCount[plan] || minMemberCount;
                                const totalSeats = memberCount + 1; // +1 for admin
                                const planAmount = getPrice(plan);
                                const monthlyPrice = totalSeats * planAmount;

                                return (
                                  <>
                                    <InputNumber
                                      min={minMemberCount}
                                      max={1000}
                                      value={memberCount}
                                      onChange={(value) => {
                                        setUpgradeMemberCount(prev => ({
                                          ...prev,
                                          [plan]: value || minMemberCount
                                        }));
                                      }}
                                      controls={true}
                                      className="w-full"
                                      style={{ width: '100%' }}
                                      size="small"
                                    />
                                    <Text className="text-xs block mt-1 text-gray-400">
                                      Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                                    </Text>
                                    <Text className="text-xs block mt-0.5 text-gray-400">
                                      Total seats: {totalSeats} (1 admin + {memberCount} members)
                                    </Text>
                                    <Divider className="!my-1.5 !border-gray-300/50" />
                                    <div className="text-center">
                                      <Text className="text-xs block text-gray-400">
                                        Monthly Total
                                      </Text>
                                      <Text className={`text-base font-bold block ${
                                        plan === 'premium' ? 'text-purple-300' : 'text-blue-300'
                                      }`}>
                                        {symbol}{monthlyPrice.toFixed(2)}/month
                                      </Text>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        <Button
                          type={plan === 'premium' || plan === 'standard' ? 'primary' : 'default'}
                          size="large"
                          block
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlanSelection(plan);
                          }}
                          disabled={processingPlan !== null}
                          loading={processingPlan === plan}
                          className={
                            plan === 'premium'
                              ? 'bg-purple-600 hover:bg-purple-700 border-purple-600'
                              : plan === 'standard'
                              ? 'bg-blue-600 hover:bg-blue-700 border-blue-600'
                              : 'bg-white/10 hover:bg-white/20 border-white/20'
                          }
                          style={{ borderRadius: '8px', height: '40px', fontSize: '14px', fontWeight: '600' }}
                          icon={processingPlan === plan ? null : <CreditCard className="w-4 h-4" />}
                        >
                          {processingPlan === plan ? 'Processing...' : selectedPlanForRenewal === plan ? 'Proceed to Payment' : 'Select Plan'}
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Coupon Code Section - Always visible */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-3 p-3 rounded-lg border bg-white/5 border-white/10"
              >
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Have a coupon or promotion code? (Optional)
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setCouponCode(value);

                        // Clear previous timeout
                        if (couponTimeoutRef.current) {
                          clearTimeout(couponTimeoutRef.current);
                        }

                        // Verify after user stops typing (debounce)
                        if (value.trim() === '') {
                          setActualCouponId(null);
                          setCouponValid(null);
                          setCouponDiscount(null);
                        } else {
                          couponTimeoutRef.current = setTimeout(() => {
                            verifyCouponCode(value);
                          }, 800);
                        }
                      }}
                      onBlur={() => {
                        if (couponCode.trim() !== '') {
                          verifyCouponCode(couponCode);
                        }
                      }}
                      placeholder="Enter coupon code"
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                      disabled={couponVerifying}
                    />
                    {couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      </div>
                    )}
                  </div>
                </div>
                {couponValid === true && couponDiscount && (
                  <Text className="text-xs mt-1.5 block font-medium text-green-400">
                    ✅ Valid! {couponDiscount} will be applied at checkout
                  </Text>
                )}
                {couponValid === false && (
                  <Text className="text-xs mt-1.5 block text-red-400">
                    ❌ Invalid coupon code. Please check and try again.
                  </Text>
                )}
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </Modal>,
    document.body
  );
}
